import { useId, useState } from 'react';

import { Box, Card } from '@zillow/constellation-10';

import { AccountViewListingGroupType } from '@zg-rentals/ts-rental-manager-api-client';

import { getFormattedAddress, isMultiplexListing } from '../../../utils/flexiblePropertyCardUtils';

import EditMultiplexPropertyModal from '../../../components/modals/EditMultiplexPropertyModal';
import FlexibleArchivePropertyModal from '../../../components/modals/FlexibleArchivePropertyModal';
import PropertyDetailsModal from '../../../components/modals/PropertyDetailsModal';

import { cardStyles, unitsTableContainerStyles } from '../styles';

import FlexiblePropertyCardHeader from './FlexiblePropertyCardHeader';
import UnitsTable from './UnitsTable';

import type { AccountViewListingGroupDto, RentalManagerAddressDto } from '@zg-rentals/ts-rental-manager-api-client';

type Props = {
  listing: AccountViewListingGroupDto;
  isLoading?: boolean;
};

type ArchiveTarget = {
  listingAlias: string;
  groupId: string;
  address: RentalManagerAddressDto;
  type: AccountViewListingGroupType;
};

const FlexiblePropertyCard = ({ listing, isLoading = false }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<ArchiveTarget | null>(null);
  const [showMultiplexEditModal, setShowMultiplexEditModal] = useState(false);
  const [showUnitsEditModal, setShowUnitsEditModal] = useState(false);
  const [editListingAlias, setEditListingAlias] = useState('');
  const panelId = useId();
  const isMultiplex = isMultiplexListing(listing);

  const toggleExpand = () => {
    setIsExpanded((current) => !current);
  };

  const handleCardClick = () => {
    toggleExpand();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // Only toggle when the card itself is focused, not a nested control.
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpand();
    }
  };

  const expandLabel = isExpanded ? `Collapse ${listing.name}` : `Expand ${listing.name}`;

  return (
    <Card.Root
      data-testid="FlexiblePropertyCard"
      interactive={true}
      tone="neutral"
      outlined={true}
      elevated={false}
      tabIndex={0}
      aria-expanded={isExpanded}
      // Only reference the panel when expanded, since it isn't mounted while collapsed.
      aria-controls={isExpanded ? panelId : undefined}
      aria-label={expandLabel}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      css={cardStyles}
    >
      <FlexiblePropertyCardHeader
        listing={listing}
        isExpanded={isExpanded}
        isLoading={isLoading}
        onArchiveClick={(listingAlias, address) =>
          setArchiveTarget({ listingAlias, groupId: listing.groupId, address, type: listing.type })
        }
        onEditClick={
          isMultiplex
            ? () => {
                setShowMultiplexEditModal(true);
              }
            : () => {
                setEditListingAlias(listing.listingAlias);
                setShowUnitsEditModal(true);
              }
        }
      />

      {isExpanded && (
        /*
         * onClick only stops clicks inside the UnitsTable (including its portaled popovers/menus
         * that bubble up) from toggling the card. It's not an interactive control,
         * so the accessibility rule's role/keyboard requirement doesn't apply here.
         */
        // eslint-disable-next-line constellation/accessibility
        <Box
          id={panelId}
          data-testid="UnitsTableContainer"
          onClick={(event) => event.stopPropagation()}
          css={unitsTableContainerStyles}
        >
          <UnitsTable
            listing={listing}
            onArchiveClick={(listingAlias, address) =>
              setArchiveTarget({
                listingAlias,
                groupId: listing.groupId,
                address,
                type: AccountViewListingGroupType.LISTING,
              })
            }
            onEditClick={(listingAlias) => {
              setEditListingAlias(listingAlias);
              setShowUnitsEditModal(true);
            }}
          />
        </Box>
      )}

      {archiveTarget && (
        /*
         * The modal renders in a portal, but its click events still bubble through the React tree
         * to the card's onClick; stop them here so interacting with the modal doesn't toggle the
         * card. It's not an interactive control, so the accessibility rule doesn't apply.
         */
        // eslint-disable-next-line constellation/accessibility
        <Box onClick={(event) => event.stopPropagation()}>
          <FlexibleArchivePropertyModal
            onClose={() => setArchiveTarget(null)}
            isArchived={false}
            listingAlias={archiveTarget.listingAlias}
            groupId={archiveTarget.groupId}
            address={archiveTarget.address}
            listingType={archiveTarget.type}
          />
        </Box>
      )}

      {showMultiplexEditModal && (
        // eslint-disable-next-line constellation/accessibility
        <Box onClick={(event) => event.stopPropagation()}>
          <EditMultiplexPropertyModal
            isOpen={true}
            groupId={listing.groupId}
            fullAddress={getFormattedAddress(listing)}
            address={listing.address}
            unitCount={listing.listingCount?.total}
            onClose={() => setShowMultiplexEditModal(false)}
            onSuccess={() => setShowMultiplexEditModal(false)}
          />
        </Box>
      )}

      {showUnitsEditModal && (
        // eslint-disable-next-line constellation/accessibility
        <Box onClick={(event) => event.stopPropagation()}>
          <PropertyDetailsModal
            isOpen={true}
            listingAlias={editListingAlias}
            submitLabel="Save changes"
            onClose={() => setShowUnitsEditModal(false)}
            onConfirm={() => setShowUnitsEditModal(false)}
          />
        </Box>
      )}
    </Card.Root>
  );
};

export default FlexiblePropertyCard;
